import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { isEmpty } from 'lodash';
import { FORM_MODES } from 'config/formModes';
import { useEntityType } from 'hooks/index';
import { CAMPAIGNS_ROUTE, PATH_TO_ENTITY_MAP } from 'modules/campaigns/config';
import { useFormDataContext } from 'modules/taboola-common-frontend-modules/formData';
import { useFormState } from 'modules/taboola-common-frontend-modules/forms';
import { useBulkOperationsApi, useCampaignGroupApi } from 'services/api';
import { useMultiLayerPathParams } from '../../../../multi-layer-drawer/hooks/useMultiLayerPathParams';
import { fetchCampaignsGroupForEdit } from '../../campaigns-group-form/flows';
import { invalidateSubCountryData } from '../actions';
import { useReportReachToGTM } from '../components/ReachEstimator/hooks/useReportReachToGTM';
import { fetchCampaignForEdit, saveCampaign } from '../flows';
import { fetchCampaignsForEdit } from '../flows/fetchCampaignsForEdit';

const useCampaignEditorState = () => {
    const {
        mode,
        formAccount: { accountName },
        fetchedData: originalCampaign,
    } = useFormDataContext();
    const dispatch = useDispatch();
    const entityType = useEntityType(PATH_TO_ENTITY_MAP);
    const { campaignId, formEntityIds } = useMultiLayerPathParams();
    const campaignGroupApi = useCampaignGroupApi();
    const bulkOperationsApi = useBulkOperationsApi();

    const formDataFetcher = useCallback(async () => {
        const isEntityEditorForm = !isEmpty(formEntityIds);
        if (isEntityEditorForm) {
            const selectedCampaignIds = formEntityIds.join(',');
            const campaigns = await dispatch(
                fetchCampaignsForEdit(accountName, selectedCampaignIds, bulkOperationsApi)
            );

            return campaigns;
        }

        const campaign = await dispatch(fetchCampaignForEdit(accountName, campaignId, entityType));
        const { campaignGroupInfo } = campaign;
        if (campaignGroupInfo?.campaignGroupId) {
            const campaignGroup = await dispatch(
                fetchCampaignsGroupForEdit(accountName, campaignGroupInfo.campaignGroupId, entityType, campaignGroupApi)
            );
            return {
                ...campaign,
                campaignGroupInfo: {
                    campaignGroupId: campaignGroupInfo.campaignGroupId,
                    campaignsGroupBudgetDistributionMethod: campaignGroup.budgetDistributionMethod,
                },
            };
        }
        return campaign;
    }, [formEntityIds, dispatch, accountName, campaignId, entityType, bulkOperationsApi, campaignGroupApi]);
    const { submit } = useFormState({ formDataFetcher });
    const { reportReachToGTM } = useReportReachToGTM();

    const handleCampaignSubmitWrapper = useCallback(() => {
        const successUrl = mode !== FORM_MODES.BULK_EDIT && CAMPAIGNS_ROUTE;
        const saveCampaignWrapper = campaign =>
            dispatch(
                saveCampaign({
                    accountName,
                    originalCampaign,
                    editedCampaign: campaign,
                    entityType,
                    mode,
                })
            );
        submit(saveCampaignWrapper, successUrl);
        reportReachToGTM(originalCampaign.id);
    }, [reportReachToGTM, submit, dispatch, accountName, originalCampaign, entityType, mode]);

    //clearing resources
    useEffect(
        () => () => {
            dispatch(invalidateSubCountryData());
        },
        [dispatch]
    );

    return {
        submit: handleCampaignSubmitWrapper,
    };
};

export default useCampaignEditorState;
